import { Constructor, Filter } from '@skylab-shared/types';
import { compareTuple } from '@skylab-shared/utils';
import { BusinessObjectBase } from '../../../bo';
import { ModelBase, PropertyType } from './../../../model';
import { ObservableCollection } from './../../../observable';
import { CanExecuteEngine } from './can-execute-engine';

//#region Definition

export interface RuleDefinition {
    readonly id: string;
    readonly triggeredBy: PropertyType<ModelBase>[];
    readonly effectOn: (Function | EffectOnAction<ObservableCollection<unknown>>)[];
    readonly forCollection: boolean;
    readonly rule: Rule;
}

export interface Rule {
    func: RuleFuncAsync<BusinessObjectBase<ModelBase>, ModelBase, ModelBase>;
}

export type RuleFuncAsync<TBo extends BusinessObjectBase<TBoModel>,
                          TBoModel extends ModelBase,
                          TSourceModel extends ModelBase> = (ctx: RuleContext<TSourceModel, TBoModel, TBo>) => Promise<boolean>;

export type RuleFunc<TBo extends BusinessObjectBase<TBoModel>,
                     TBoModel extends ModelBase,
                     TSourceModel extends ModelBase> = (ctx: RuleContext<TSourceModel, TBoModel, TBo>) => boolean;

export interface RuleContext<TSourceModel extends ModelBase = ModelBase,
                             TBoModel extends ModelBase = ModelBase,
                             TBo extends BusinessObjectBase<TBoModel> = BusinessObjectBase<TBoModel>> {
    readonly bo: TBo;
    /**
     * The object whose change triggers rule executing.
     */
    readonly source: Source<TSourceModel, CanExecuteEngine<TBo, TBoModel>>;
    /**
     * The object on which result of rule executing will be set.
     */
    readonly target: Target<TBo>;
}

export type Source<TSourceModel = ModelBase, TEngine = CanExecuteEngine<BusinessObjectBase<ModelBase>, ModelBase>> = TSourceModel | TEngine;
export type Target<TBo = BusinessObjectBase<ModelBase>> = TBo | ObservableCollection<ModelBase>;

export type EffectOnAction<TEffectOnType> = [Constructor<TEffectOnType>, ActionsFor<TEffectOnType>];

//#region ObservableCollection

export const ValidObservableCollectionActions = {
    add: <ObservableCollectionMethods[]> ['insert', 'push', 'unshift'],
    remove: <ObservableCollectionMethods[]> ['pop', 'clear', 'remove', 'removeAt', 'shift']
};

type ObservableCollectionMethods = keyof ObservableCollection<unknown>;

//#endregion

type ActionsFor<TType> = TType extends ObservableCollection<unknown>
    ? keyof typeof ValidObservableCollectionActions
    : TType extends BusinessObjectBase<ModelBase>
        ? keyof Filter<TType, Function>
        : never;

//#endregion

//#region Attached rule

export class AttachedRule {
    public constructor( protected readonly ruleDefinition: RuleDefinition,
                        protected readonly bo: BusinessObjectBase<ModelBase>,
                        protected readonly source: Source,
                        protected readonly target: Target) { }

    public get ruleId(): string {
        return this.ruleDefinition.id;
    }

    public attachedToSource(source: Source): boolean {
        return this.source === source;
    }

    public attachedToTarget(target: Target): boolean {
        return this.target === target;
    }

    public triggeredBy(property: PropertyType<ModelBase>): boolean {
        return this.ruleDefinition.triggeredBy.some(triggeredByProperty => compareTuple(triggeredByProperty, property));
    }

    public async invoke(): Promise<CanExecuteRuleResult[]> {
        const violation = await this.validateRule();
        return this.ruleDefinition.effectOn.map(action => ({
            ...violation,
            action: action
        }));
    }

    private async validateRule(): Promise<Omit<CanExecuteRuleResult, 'action'>> {
        const ctx = { bo: this.bo, source: this.source, target: this.target };
        const canExecute = await this.ruleDefinition.rule.func(ctx);
        return {
            source: this.target,
            canExecute,
            id: this.ruleId
        };
    }
}

//#endregion

//#region Result

export interface CanExecuteRuleResult {
    readonly id: string;
    readonly source: unknown;
    readonly action: (Function | EffectOnAction<ObservableCollection<unknown>>);
    readonly canExecute: boolean;
}

//#endregion
